import * as React from "react";

import { cn } from "@/shared/lib/css";
import { useIsMobile } from "@/shared/lib/use-mobile";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/kit/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "@/shared/ui/kit/drawer";

import { DIALOG_TOKENS } from "./dialog-tokens";

// ============================================================================
// Types
// ============================================================================

export interface ResponsiveModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  children: React.ReactNode;
}

export interface ResponsiveModalContentProps {
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean;
}

export interface ResponsiveModalHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export interface ResponsiveModalFooterProps {
  className?: string;
  children: React.ReactNode;
}

export interface ResponsiveModalTitleProps {
  className?: string;
  children: React.ReactNode;
}

export interface ResponsiveModalDescriptionProps {
  className?: string;
  children: React.ReactNode;
}

export interface ResponsiveModalBodyProps {
  className?: string;
  children: React.ReactNode;
}

export interface ResponsiveModalTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export interface ResponsiveModalCloseProps {
  children: React.ReactNode;
  className?: string;
}

// ============================================================================
// Context
// ============================================================================

const ResponsiveModalContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
});

function useResponsiveModalContext() {
  return React.useContext(ResponsiveModalContext);
}

// ============================================================================
// Components
// ============================================================================

function ResponsiveModalRoot({
  open,
  onOpenChange,
  modal = true,
  children,
}: ResponsiveModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <ResponsiveModalContext.Provider value={{ isMobile: true }}>
        <Drawer open={open} onOpenChange={onOpenChange} modal={modal}>
          {children}
        </Drawer>
      </ResponsiveModalContext.Provider>
    );
  }

  return (
    <ResponsiveModalContext.Provider value={{ isMobile: false }}>
      <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
        {children}
      </Dialog>
    </ResponsiveModalContext.Provider>
  );
}

function ResponsiveModalContent({
  className,
  children,
  showCloseButton = true,
}: ResponsiveModalContentProps) {
  const { isMobile } = useResponsiveModalContext();

  // On mobile: remove max-w-* classes to allow full width
  const mobileClassName = className?.replace(/max-w-\[?\w*\]?/g, "").trim();

  if (isMobile) {
    return (
      <DrawerContent
        className={cn(
          "max-h-[90dvh]",
          "w-full",
          "flex flex-col",
          mobileClassName,
        )}
      >
        {children}
      </DrawerContent>
    );
  }

  return (
    <DialogContent
      className={cn(
        DIALOG_TOKENS.dialogMaxWidth,
        DIALOG_TOKENS.dialogMaxHeight,
        DIALOG_TOKENS.dialogPadding,
        "flex flex-col",
        !showCloseButton && "[&>button]:hidden",
        className,
      )}
    >
      {children}
    </DialogContent>
  );
}

function ResponsiveModalHeader({
  className,
  children,
}: ResponsiveModalHeaderProps) {
  return (
    <div
      className={cn(
        DIALOG_TOKENS.headerPadding,
        DIALOG_TOKENS.headerBorder,
        className,
      )}
    >
      {children}
    </div>
  );
}

function ResponsiveModalFooter({
  className,
  children,
}: ResponsiveModalFooterProps) {
  return (
    <div className={cn(DIALOG_TOKENS.footerPadding, className)}>{children}</div>
  );
}

function ResponsiveModalTitle({
  className,
  children,
}: ResponsiveModalTitleProps) {
  const { isMobile } = useResponsiveModalContext();

  if (isMobile) {
    return (
      <DrawerTitle className={cn(DIALOG_TOKENS.titleSize, className)}>
        {children}
      </DrawerTitle>
    );
  }

  return (
    <DialogTitle className={cn(DIALOG_TOKENS.titleSize, className)}>
      {children}
    </DialogTitle>
  );
}

function ResponsiveModalDescription({
  className,
  children,
}: ResponsiveModalDescriptionProps) {
  const { isMobile } = useResponsiveModalContext();

  if (isMobile) {
    return (
      <DrawerDescription
        className={cn("text-sm text-muted-foreground", className)}
      >
        {children}
      </DrawerDescription>
    );
  }

  return (
    <DialogDescription
      className={cn("text-sm text-muted-foreground", className)}
    >
      {children}
    </DialogDescription>
  );
}

function ResponsiveModalBody({
  className,
  children,
}: ResponsiveModalBodyProps) {
  return (
    <div
      className={cn(
        DIALOG_TOKENS.contentScrollable,
        DIALOG_TOKENS.contentPadding,
        className,
      )}
    >
      {children}
    </div>
  );
}

function ResponsiveModalTrigger({
  children,
  asChild,
}: ResponsiveModalTriggerProps) {
  const { isMobile } = useResponsiveModalContext();

  if (isMobile) {
    return <DrawerTrigger asChild={asChild}>{children}</DrawerTrigger>;
  }

  return <DialogTrigger asChild={asChild}>{children}</DialogTrigger>;
}

function ResponsiveModalClose({
  children,
  className,
}: ResponsiveModalCloseProps) {
  const { isMobile } = useResponsiveModalContext();

  if (isMobile) {
    return <DrawerClose className={className}>{children}</DrawerClose>;
  }

  return <DialogClose className={className}>{children}</DialogClose>;
}

// ============================================================================
// Exports
// ============================================================================

export const ResponsiveModal = Object.assign(ResponsiveModalRoot, {
  Content: ResponsiveModalContent,
  Header: ResponsiveModalHeader,
  Footer: ResponsiveModalFooter,
  Body: ResponsiveModalBody,
  Title: ResponsiveModalTitle,
  Description: ResponsiveModalDescription,
  Trigger: ResponsiveModalTrigger,
  Close: ResponsiveModalClose,
});

export {
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalFooter,
  ResponsiveModalBody,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalTrigger,
  ResponsiveModalClose,
};

export { useResponsiveModalContext as useResponsiveModal };
